import { useState, useEffect, useRef, useCallback } from 'react';

const QUICK_EMOJIS = ['😂', '🔥', '💀', '👏', '😎', '🎉', '💪', '😱'];

// ===== Room Chat Bubble (in-game overlay) =====
export const RoomChatOverlay = ({ messages, onSendEmoji, playerName }) => {
  const [showChat, setShowChat] = useState(false);
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!inputText.trim()) return;
    onSendEmoji(inputText.trim(), false);
    setInputText('');
  };

  if (!showChat) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); setShowChat(true); }}
        className="absolute bottom-4 left-4 z-[45] w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110 active:scale-95"
        style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.15)' }}
        data-testid="chat-toggle-btn"
      >
        <span className="text-lg">💬</span>
        {messages.length > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
            {messages.length > 9 ? '9+' : messages.length}
          </span>
        )}
      </button>
    );
  }

  return (
    <div
      className="absolute bottom-4 left-4 z-[45] w-64 rounded-2xl overflow-hidden"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.1)' }}
      onClick={(e) => e.stopPropagation()}
      data-testid="chat-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
        <span className="text-white/80 text-xs font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>Chat</span>
        <button onClick={() => setShowChat(false)} className="text-white/40 hover:text-white/80 text-sm">✕</button>
      </div>

      {/* Messages */}
      <div className="h-28 overflow-y-auto px-3 py-2 space-y-1.5 scrollbar-thin">
        {messages.length === 0 && (
          <p className="text-white/30 text-[10px] text-center mt-4" style={{ fontFamily: 'Nunito, sans-serif' }}>No messages yet</p>
        )}
        {messages.map((msg) => (
          <div key={msg.id} className={`flex items-start gap-1.5 ${msg.name === playerName ? 'flex-row-reverse' : ''}`}>
            <div className={`max-w-[80%] px-2.5 py-1.5 rounded-xl ${msg.name === playerName ? 'bg-amber-500/30' : 'bg-white/10'}`}>
              <span className="text-amber-400/70 text-[9px] font-bold block" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                {msg.name === playerName ? 'You' : msg.name}
              </span>
              <span className={`${msg.is_emoji ? 'text-2xl' : 'text-white/90 text-xs'}`} style={{ fontFamily: 'Nunito, sans-serif' }}>
                {msg.text}
              </span>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Emojis */}
      <div className="flex gap-1 px-3 py-1.5 border-t border-white/10 overflow-x-auto">
        {QUICK_EMOJIS.map((emoji) => (
          <button
            key={emoji}
            onClick={() => onSendEmoji(emoji, true)}
            className="text-lg hover:scale-125 transition-transform flex-shrink-0"
          >
            {emoji}
          </button>
        ))}
      </div>

      {/* Text input */}
      <div className="flex gap-1.5 px-3 py-2 border-t border-white/10 items-center">
        <input
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          placeholder="Type..."
          maxLength={100}
          className="flex-1 bg-white/10 rounded-lg px-2.5 py-1.5 text-white text-xs outline-none placeholder-white/30"
          style={{ fontFamily: 'Nunito, sans-serif' }}
          data-testid="chat-input"
        />
        <button
          onClick={handleSend}
          className="px-2.5 py-1.5 rounded-lg text-xs font-bold text-slate-900 transition-all hover:scale-105"
          style={{ background: 'linear-gradient(135deg, #fbbf24, #f59e0b)', fontFamily: 'Fredoka, sans-serif' }}
          data-testid="chat-send-btn"
        >
          Send
        </button>
      </div>
    </div>
  );
};


// ===== Floating Emoji Reaction (appears on screen when someone sends emoji) =====
export const FloatingEmoji = ({ emoji, x, y }) => {
  return (
    <div
      className="absolute pointer-events-none z-[44] text-4xl"
      style={{
        left: `${x}%`,
        top: `${y}%`,
        animation: 'emojiFloat 2s ease-out forwards',
      }}
    >
      {emoji}
      <style>{`
        @keyframes emojiFloat {
          0% { opacity: 1; transform: scale(0.5) translateY(0); }
          30% { opacity: 1; transform: scale(1.3) translateY(-20px); }
          100% { opacity: 0; transform: scale(0.8) translateY(-80px); }
        }
      `}</style>
    </div>
  );
};


// ===== Room Lobby Screen =====
export const RoomLobby = ({
  room, playerId, messages, floatingEmojis, connected,
  onSendMessage, onReady, onStartGame, onLeave,
  isReady, playerCharacter,
}) => {
  const isHost = room?.host_id === playerId;

  // Safe toast notification
  const showToast = (text) => {
    const toast = document.createElement('div');
    toast.style.cssText = 'position:fixed;top:60px;left:50%;transform:translateX(-50%);z-index:9999;background:#22c55e;color:#fff;padding:8px 20px;border-radius:10px;font-family:Nunito,sans-serif;font-size:13px;box-shadow:0 4px 12px rgba(0,0,0,0.3);';
    toast.textContent = text;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 2000);
  };

  // Fallback copy using textarea (works in all browsers)
  const fallbackCopy = (text) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;left:-9999px;';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      showToast('Copied!');
    } catch {
      showToast('Code: ' + (room?.code || ''));
    }
  };

  return (
    <div
      className="relative w-full max-w-md h-[100dvh] sm:h-[700px] mx-auto overflow-hidden shadow-2xl sm:rounded-3xl sm:border-8 sm:border-white select-none flex flex-col"
      style={{ background: 'linear-gradient(160deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)' }}
      data-testid="room-lobby"
    >
      {/* Floating emojis */}
      {floatingEmojis.map((fe) => (
        <FloatingEmoji key={fe.id} emoji={fe.emoji} x={fe.x} y={fe.y} />
      ))}

      {/* Header */}
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={onLeave}
            className="text-white/50 hover:text-white/80 text-sm transition-colors"
            style={{ fontFamily: 'Nunito, sans-serif' }}
            data-testid="leave-room-btn"
          >
            ← Leave
          </button>
          <div className="text-center">
            <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Room
            </h2>
          </div>
          <div className="w-12" />
        </div>

        {/* Room Code + Share */}
        <div className="flex items-center justify-center gap-2 mb-3">
          <div className="px-5 py-2.5 rounded-xl bg-white/5 border border-white/10">
            <span className="text-amber-400 text-2xl font-bold tracking-widest" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="room-code">
              {room?.code || '----'}
            </span>
          </div>
          <button
            onClick={() => {
              const code = room?.code || '';
              try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                  navigator.clipboard.writeText(code).then(() => {
                    showToast('Code copied!');
                  }).catch(() => {
                    fallbackCopy(code);
                  });
                } else {
                  fallbackCopy(code);
                }
              } catch {
                fallbackCopy(code);
              }
            }}
            className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-all"
            data-testid="copy-code-btn"
            title="Copy code"
          >
            📋
          </button>
          <button
            onClick={() => {
              const code = room?.code || '';
              const joinUrl = `${window.location.origin}?join=${code}`;
              const inviteText = `Let's play Mimi Fly together! 🚀🎈\nJump in with this code: ${code}\n${joinUrl}`;
              try {
                if (navigator.share) {
                  navigator.share({
                    title: 'Mimi Fly - Let\'s play! 🚀🎈',
                    text: `Let's play Mimi Fly together! 🚀🎈\nJump in with this code: ${code}`,
                    url: joinUrl,
                  }).catch(() => {});
                } else if (navigator.clipboard && navigator.clipboard.writeText) {
                  navigator.clipboard.writeText(inviteText).then(() => {
                    showToast('Invite link copied!');
                  }).catch(() => {
                    fallbackCopy(inviteText);
                  });
                } else {
                  fallbackCopy(inviteText);
                }
              } catch {
                fallbackCopy(inviteText);
              }
              try {
                if ('Notification' in window && Notification.permission === 'default') {
                  Notification.requestPermission().catch(() => {});
                }
              } catch { /* ignore */ }
            }}
            className="w-10 h-10 rounded-xl bg-green-500/20 border border-green-500/30 flex items-center justify-center text-green-400 hover:bg-green-500/30 transition-all hover:scale-110 active:scale-95"
            data-testid="share-invite-btn"
            title="Share invite"
          >
            📤
          </button>
        </div>

        {/* WhatsApp + JustTalk Kids Quick Share */}
        <div className="flex items-center justify-center gap-2 mb-4">
          <button
            onClick={() => {
              const code = room?.code || '';
              const joinUrl = `${window.location.origin}?join=${code}`;
              const msg = encodeURIComponent(`Let's play Mimi Fly together! 🚀🎈\nJump in with this code: ${code}\n${joinUrl}`);
              window.open(`https://wa.me/?text=${msg}`, '_blank');
            }}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-white text-xs font-bold transition-all hover:scale-105 active:scale-95"
            style={{
              background: 'linear-gradient(135deg, #25D366, #128C7E)',
              fontFamily: 'Fredoka, sans-serif',
              boxShadow: '0 3px 0 #075E54',
            }}
            data-testid="whatsapp-invite-btn"
          >
            <span className="text-base">📞</span> WhatsApp
          </button>
          <button
            onClick={() => {
              const code = room?.code || '';
              const joinUrl = `${window.location.origin}?join=${code}`;
              const msg = encodeURIComponent(`Let's play Mimi Fly together! 🚀🎈\nJump in with this code: ${code}\n${joinUrl}`);
              // Try JustTalk Kids web share URL, fallback to clipboard
              window.open(`https://kids.justalk.com/share?text=${msg}`, '_blank');
            }}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-white text-xs font-bold transition-all hover:scale-105 active:scale-95"
            style={{
              background: 'linear-gradient(135deg, #FF6B9D, #C850C0)',
              fontFamily: 'Fredoka, sans-serif',
              boxShadow: '0 3px 0 #9B3675',
            }}
            data-testid="justtalk-invite-btn"
          >
            <span className="text-base">📞</span> JustTalk Kids
          </button>
        </div>

        <p className="text-white/30 text-[11px] text-center mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>
          Share the link — friends join instantly!
        </p>
        {!connected && (
          <p className="text-amber-400/80 text-[11px] text-center mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Connecting...
          </p>
        )}
      </div>

      {/* Players */}
      <div className="px-5 flex-1">
        <div className="space-y-2 mb-3">
          {room?.players?.map((player) => (
            <div
              key={player.id}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                player.id === playerId ? 'bg-amber-400/10 border border-amber-400/30' : 'bg-white/5 border border-white/5'
              }`}
              data-testid={`lobby-player-${player.id}`}
            >
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-lg">
                {player.character === 'mimi' ? '🎈' : player.character === 'juma' ? '🟡' : player.character === 'soni' ? '🟢' : '🔵'}
              </div>
              <span className="flex-1 text-white text-sm font-bold truncate" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                {player.name} {player.id === playerId && '(You)'} {player.id === room.host_id && '👑'}
              </span>
              {player.ready && (
                <span className="text-green-400 text-xs font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>Ready</span>
              )}
            </div>
          ))}
          {/* Empty slots */}
          {room && [...Array(Math.max(0, room.max_players - (room.players?.length || 0)))].map((_, i) => (
            <div key={`empty-${i}`} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.02] border border-dashed border-white/10">
              <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-white/20 text-sm">?</div>
              <span className="text-white/20 text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>Waiting...</span>
            </div>
          ))}
        </div>

        <p className="text-white/20 text-[10px] text-center mt-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
          Chat with your friends while waiting
        </p>

        {/* Quick Emojis */}
        <div className="mt-3 space-y-2">
          <div className="flex gap-1 px-2 overflow-x-auto">
            {['😂', '🔥', '💀', '👏', '😎', '🎉', '💪', '😱'].map((emoji) => (
              <button
                key={emoji}
                onClick={() => onSendMessage(emoji, true)}
                className="text-lg hover:scale-125 transition-transform flex-shrink-0 p-0.5"
                data-testid={`lobby-emoji-${emoji}`}
              >
                {emoji}
              </button>
            ))}
          </div>
          {/* Recent messages */}
          {messages.length > 0 && (
            <div className="max-h-20 overflow-y-auto px-2 space-y-1">
              {messages.slice(-5).map((msg) => (
                <div key={msg.id} className="flex items-center gap-1.5">
                  <span className="text-amber-400/60 text-[9px] font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    {msg.name}:
                  </span>
                  <span className={`${msg.is_emoji ? 'text-lg' : 'text-white/70 text-[11px]'}`}>
                    {msg.text}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bottom actions */}
      <div className="px-5 pb-5 flex gap-2">
        {!isHost && (
          <button
            onClick={() => onReady(!isReady)}
            className={`flex-1 py-3.5 rounded-xl text-base font-bold transition-all hover:scale-105 active:scale-95 ${
              isReady ? 'bg-green-500 text-white' : 'bg-white/10 text-white border border-white/20'
            }`}
            style={{ fontFamily: 'Fredoka, sans-serif' }}
            data-testid="ready-btn"
          >
            {isReady ? 'Ready!' : 'Ready Up'}
          </button>
        )}
        {isHost && (
          <button
            onClick={onStartGame}
            disabled={!connected}
            className="flex-1 py-3.5 rounded-xl text-base font-bold text-slate-900 transition-all hover:scale-105 active:scale-95 disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg, #fbbf24, #f59e0b)', fontFamily: 'Fredoka, sans-serif' }}
            data-testid="start-game-btn"
          >
            {connected ? 'Start Game' : 'Connecting...'}
          </button>
        )}
      </div>
    </div>
  );
};


// ===== Create/Join Room Screen =====
export const PlayWithFriendsScreen = ({ playerName, playerCharacter, onBack, onRoomJoined }) => {
  const [tab, setTab] = useState('create'); // 'create' or 'join'
  const [joinCode, setJoinCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleCreate = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/rooms/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: playerName, character: playerCharacter, player_id: `p_${Date.now()}` }),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); setLoading(false); return; }
      onRoomJoined(data.code, data.player_id);
    } catch (e) {
      setError('Failed to create room');
    }
    setLoading(false);
  };

  const handleJoin = async () => {
    if (!joinCode.trim()) { setError('Enter a room code'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/rooms/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: joinCode.trim(), name: playerName, character: playerCharacter, player_id: `p_${Date.now()}` }),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); setLoading(false); return; }
      onRoomJoined(data.code, data.player_id);
    } catch (e) {
      setError('Failed to join room');
    }
    setLoading(false);
  };

  return (
    <div
      className="relative w-full max-w-md h-[100dvh] sm:h-[700px] mx-auto overflow-hidden shadow-2xl sm:rounded-3xl sm:border-8 sm:border-white select-none flex flex-col items-center justify-center px-6"
      style={{ background: 'linear-gradient(160deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)' }}
      data-testid="play-with-friends-screen"
    >
      {/* Back button */}
      <button
        onClick={onBack}
        className="absolute top-5 left-5 text-white/50 hover:text-white/80 text-sm transition-colors z-10"
        style={{ fontFamily: 'Nunito, sans-serif' }}
        data-testid="pwf-back-btn"
      >
        ← Back
      </button>

      <div className="text-4xl mb-2">🎮</div>
      <h1 className="text-3xl font-bold text-white mb-1" style={{ fontFamily: 'Fredoka, sans-serif' }}>
        Play with Friends
      </h1>
      <p className="text-white/40 text-sm mb-6" style={{ fontFamily: 'Nunito, sans-serif' }}>
        Create or join a room
      </p>

      {/* Tab selector */}
      <div className="flex w-full max-w-xs rounded-xl overflow-hidden mb-5 border border-white/10">
        <button
          onClick={() => { setTab('create'); setError(''); }}
          className={`flex-1 py-2.5 text-sm font-bold transition-all ${tab === 'create' ? 'bg-amber-500 text-slate-900' : 'bg-white/5 text-white/50'}`}
          style={{ fontFamily: 'Fredoka, sans-serif' }}
          data-testid="tab-create"
        >
          Create Room
        </button>
        <button
          onClick={() => { setTab('join'); setError(''); }}
          className={`flex-1 py-2.5 text-sm font-bold transition-all ${tab === 'join' ? 'bg-amber-500 text-slate-900' : 'bg-white/5 text-white/50'}`}
          style={{ fontFamily: 'Fredoka, sans-serif' }}
          data-testid="tab-join"
        >
          Join Room
        </button>
      </div>

      <div className="w-full max-w-xs">
        {tab === 'create' ? (
          <div className="text-center">
            <p className="text-white/50 text-sm mb-5" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Create a room and share the code with your friends
            </p>
            <button
              onClick={handleCreate}
              disabled={loading}
              className="w-full py-4 rounded-xl text-lg font-bold text-slate-900 transition-all hover:scale-105 active:scale-95 disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #fbbf24, #f59e0b)', fontFamily: 'Fredoka, sans-serif', boxShadow: '0 4px 20px rgba(251,191,36,0.3)' }}
              data-testid="create-room-btn"
            >
              {loading ? 'Creating...' : 'Create Room'}
            </button>
          </div>
        ) : (
          <div className="text-center">
            <p className="text-white/50 text-sm mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Enter the room code from your friend
            </p>
            <input
              type="text"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="MIMI-XXXX"
              maxLength={9}
              className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3.5 text-white text-center text-xl tracking-widest font-bold outline-none placeholder-white/20 focus:border-amber-400/40 transition-colors mb-4"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
              data-testid="join-code-input"
            />
            <button
              onClick={handleJoin}
              disabled={loading || !joinCode.trim()}
              className="w-full py-4 rounded-xl text-lg font-bold text-slate-900 transition-all hover:scale-105 active:scale-95 disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #fbbf24, #f59e0b)', fontFamily: 'Fredoka, sans-serif', boxShadow: '0 4px 20px rgba(251,191,36,0.3)' }}
              data-testid="join-room-btn"
            >
              {loading ? 'Joining...' : 'Join Room'}
            </button>
          </div>
        )}

        {error && (
          <p className="text-red-400 text-sm text-center mt-3" style={{ fontFamily: 'Nunito, sans-serif' }} data-testid="room-error">
            {error}
          </p>
        )}
      </div>
    </div>
  );
};


// ===== useRoom Hook — manages WebSocket connection and room state =====
export const useRoom = (roomCode, playerId, apiUrl) => {
  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [floatingEmojis, setFloatingEmojis] = useState([]);
  const [connected, setConnected] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const wsRef = useRef(null);

  const disconnectIntentionalRef = useRef(false);

  const connect = useCallback(() => {
    if (!roomCode || !playerId || !apiUrl) return;
    // Don't reconnect if already connected
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

    disconnectIntentionalRef.current = false;
    const wsUrl = apiUrl.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/rooms/ws/${roomCode}/${playerId}`);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('Room WS connected');
      setConnected(true);
    };
    ws.onclose = (e) => {
      console.log('Room WS closed', e.code, e.reason);
      setConnected(false);
      // Auto-reconnect after 2s if not intentionally disconnected
      if (!disconnectIntentionalRef.current && wsRef.current === ws) {
        wsRef.current = null;
        setTimeout(() => connect(), 2000);
      }
    };
    ws.onerror = (e) => {
      console.error('Room WS error', e);
      setConnected(false);
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);

      switch (data.type) {
        case 'room_state':
          setRoom(data.room);
          if (data.messages) setMessages(data.messages);
          break;
        case 'chat':
          setMessages(prev => [...prev.slice(-49), data.message]);
          if (data.message.is_emoji) {
            const fe = {
              id: data.message.id,
              emoji: data.message.text,
              x: 20 + Math.random() * 60,
              y: 30 + Math.random() * 40,
            };
            setFloatingEmojis(prev => [...prev, fe]);
            setTimeout(() => setFloatingEmojis(prev => prev.filter(e => e.id !== fe.id)), 2000);
          }
          break;
        case 'player_joined':
        case 'player_left':
        case 'player_ready':
          setRoom(data.room);
          // Browser notification when tab is not focused
          if (data.type === 'player_joined' && document.hidden && 'Notification' in window && Notification.permission === 'granted') {
            const p = data.player;
            new Notification('Mimi Fly', { body: `${p?.name || 'Someone'} joined your room!`, icon: 'https://customer-assets.emergentagent.com/job_mimi-ultra/artifacts/vka41st1_1000050248.png' });
          }
          break;
        case 'game_start':
          setRoom(data.room);
          setGameStarted(true);
          gameStartedRef.current = true;
          break;
        case 'error':
          console.error('Room WS error:', data.message);
          break;
        default:
          break;
      }
    };
  }, [roomCode, playerId, apiUrl]);

  const gameStartedRef = useRef(false);

  useEffect(() => {
    connect();
    // Keepalive ping every 25s to prevent WebSocket idle timeout
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 25000);
    return () => {
      clearInterval(pingInterval);
      // Don't close WS if game started — it's being handed off to the game
      if (!gameStartedRef.current) {
        disconnectIntentionalRef.current = true;
        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }
      }
    };
  }, [connect]);

  const sendMessage = useCallback((text, isEmoji = false) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'chat', text, is_emoji: isEmoji }));
    }
  }, []);

  const sendReady = useCallback((ready) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'ready', ready }));
    }
  }, []);

  const sendStartGame = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'start_game' }));
    }
  }, []);

  const disconnect = useCallback(() => {
    disconnectIntentionalRef.current = true;
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setRoom(null);
    setMessages([]);
    setConnected(false);
    setGameStarted(false);
  }, []);

  const getWs = useCallback(() => wsRef.current, []);

  return {
    room, messages, floatingEmojis, connected, gameStarted,
    sendMessage, sendReady, sendStartGame, disconnect, getWs,
  };
};
